"use client"

import { useRef } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import {
  chatStickyHeaderPaddingTop,
  chatStickyIconButtonClass,
  useChatStickyHeaderHeight,
} from "@/components/chat/chat-sticky-header"
import {
  CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME,
  CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS,
} from "@/components/chat/chat-conversation-chrome-styles"
import { HUB_OVERLAY_BACKDROP, HUB_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop"
import { cn } from "@/lib/utils"

interface ChatConversationSkeletonProps {
  className?: string
  showBackButton?: boolean
  showActionButton?: boolean
  hideHeader?: boolean
  surface?: "overlay" | "panel"
}

export function ChatConversationSkeleton({
  className,
  showBackButton = true,
  showActionButton = true,
  hideHeader = false,
  surface = "overlay",
}: ChatConversationSkeletonProps) {
  const headerRef = useRef<HTMLElement>(null)
  const isPanel = surface === "panel"
  const layer = isPanel ? "absolute" : "fixed"

  useChatStickyHeaderHeight(headerRef, !hideHeader)

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-background text-foreground antialiased",
        className,
      )}
    >
      {!hideHeader && (
        <header
          ref={headerRef}
          className={cn(
            CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME,
            !isPanel && CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS,
            layer,
            isPanel ? "z-20" : "z-50",
          )}
        >
          <div
            aria-hidden
            className={cn(HUB_OVERLAY_BACKDROP, "opacity-100")}
            style={HUB_OVERLAY_MASK}
          />
          <div className="relative z-1">
            <div className="mx-auto flex min-h-12 w-full max-w-5xl items-center gap-2 px-3 md:min-h-18 md:gap-3 md:px-4 md:py-1">
              {showBackButton ? (
                <Skeleton className={cn(chatStickyIconButtonClass, "rounded-full")} />
              ) : null}
              <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">
                <Skeleton className="h-10 w-10 shrink-0 rounded-full md:h-11 md:w-11" />
                <Skeleton className="h-4 w-32 max-w-full" />
              </div>
              {showActionButton ? (
                <Skeleton className={cn(chatStickyIconButtonClass, "rounded-full")} />
              ) : null}
            </div>
          </div>
        </header>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          paddingTop: hideHeader ? undefined : chatStickyHeaderPaddingTop(),
          paddingBottom: isPanel ? 88 : 200,
        }}
      >
        <div className="space-y-3 px-4 py-2 md:px-5">
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-40 rounded-2xl" />
              </div>
            </div>
            <div className="flex justify-end">
              <Skeleton className="h-6 w-48 rounded-2xl" />
            </div>
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-56 rounded-2xl" />
            </div>
            <div className="flex justify-end">
              <Skeleton className="h-24 w-60 rounded-lg" />
            </div>
            <div className="flex items-start gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-40 rounded-2xl" />
                <Skeleton className="h-6 w-28 rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          layer,
          "bottom-0 left-0 right-0 z-40 w-full px-4 pb-4 pt-4",
          !isPanel && "mx-auto max-w-5xl",
        )}
      >
        <div className="flex items-end gap-2">
          <div className="relative flex-1">
            <Skeleton className="h-10 min-h-[36px] w-full rounded-xl" />
          </div>
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
        </div>
      </div>
    </div>
  )
}
